import * as React from 'react';
import { cn } from '../lib/cn';
import { chartColors } from '../design-system/tokens/chartColors';

/**
 * ChartBars — horizontal bars for "how do these compare".
 *
 * The form people reach for a pie to draw. Length is an easier judgement than
 * angle, long category names have somewhere to live, and a 3% row is still a
 * visible sliver rather than an 11° splinter.
 *
 * `scale` is the whole design of this component:
 *
 *   'ordinal' — the ORDER carries meaning (pipeline stages, size tiers, age
 *               bands). One hue in steps, light → dark, so the sequence is
 *               visible in the colour. Reordering the rows would change what
 *               the chart says.
 *
 *   'single'  — the NAMES are the subject (payment methods, products, regions).
 *               EVERY bar takes the same hue. Colouring these by value would
 *               spend the identity channel re-encoding what bar length already
 *               shows, and colouring them by rank would repaint the survivors
 *               whenever a filter changes the row count.
 *
 * There is no 'categorical' mode. Distinct series belong in a stack or a line,
 * where a legend can name them; a bar list is one series by construction.
 *
 * Values are rendered OUTSIDE the mark in ink tokens (DC-7). The best slot in
 * the palette reaches 3.8:1 against navy, which is under AA for body type — so
 * no number is ever set on a fill.
 */

export interface ChartBarsDatum {
  label: string;
  value: number;
  /** Muted note after the label, for a row whose basis differs from its siblings. */
  hint?: string;
}

export interface ChartBarsProps {
  data: ChartBarsDatum[];
  scale?: 'ordinal' | 'single';
  /** Formats the trailing value. Defaults to a plain locale number. */
  format?: (value: number) => string;
  /**
   * Scale bars against the largest row (default) rather than the sum. Use the
   * sum only when the rows genuinely partition a whole — and prefer ChartStack
   * for that, since it shows the whole.
   */
  normaliseToMax?: boolean;
  /** Describes the shape in words for a screen reader. Required. */
  ariaLabel: string;
  className?: string;
}

/**
 * Position `t` (0..1) along the ordinal ramp, interpolating between its stops.
 *
 * Three stops cover three rows exactly — at t = 0, 0.5 and 1 this returns the
 * validated stops themselves, unmodified. Beyond three, interpolating keeps the
 * ramp monotone (the property the reader is actually using) rather than cycling
 * back to the light end and implying stage 4 comes before stage 1.
 *
 * MEASURED CAP: 7 ROWS. The interpolated ramp was run through the ordinal
 * checks at every length — 4, 5, 6 and 7 rows all PASS; 8 FAILS on adjacent
 * lightness (four consecutive gaps land at ΔL 0.054–0.060, under the 0.06
 * floor) and the last few steps stop being tellable apart. Past 7, the answer
 * is fewer rows or a table, never a longer ramp.
 */
function rampAt(t: number): string {
  // Widened from the `as const` tuple on purpose: this function must keep
  // working if the ramp ever gains or loses a step.
  const stops: readonly string[] = chartColors.ordinal;
  const last = stops[stops.length - 1] ?? chartColors.categorical[0];
  if (stops.length < 2) return last;

  const clamped = Math.min(Math.max(t, 0), 1);
  const scaled = clamped * (stops.length - 1);
  const i = Math.min(Math.floor(scaled), stops.length - 2);
  const from = stops[i];
  const to = stops[i + 1];
  if (from === undefined || to === undefined) return last;
  return mixHex(from, to, scaled - i);
}

function mixHex(a: string, b: string, t: number): string {
  const pa = parseInt(a.slice(1), 16);
  const pb = parseInt(b.slice(1), 16);
  const ch = (p: number, shift: number) => (p >> shift) & 0xff;
  const out = (shift: number) =>
    Math.round(ch(pa, shift) + (ch(pb, shift) - ch(pa, shift)) * t);
  return `#${((out(16) << 16) | (out(8) << 8) | out(0)).toString(16).padStart(6, '0')}`;
}

export function ChartBars({
  data,
  scale = 'single',
  format = (v) => v.toLocaleString(),
  normaliseToMax = true,
  ariaLabel,
  className,
}: ChartBarsProps) {
  const total = data.reduce((sum, d) => sum + Math.max(d.value, 0), 0);
  const largest = data.reduce((m, d) => Math.max(m, d.value), 0);
  const basis = normaliseToMax ? largest : total;

  return (
    <div role="img" aria-label={ariaLabel} className={cn('flex flex-col gap-3', className)}>
      {data.map((d, i) => {
        // A non-zero row always keeps a visible sliver. A zero row shows
        // nothing at all — an empty track is the honest picture of nothing,
        // and a minimum-width stub would read as "a little".
        const raw = basis > 0 ? (d.value / basis) * 100 : 0;
        const width = d.value > 0 ? Math.max(raw, 6) : 0;
        const fill =
          scale === 'ordinal'
            ? rampAt(data.length > 1 ? i / (data.length - 1) : 1)
            : chartColors.categorical[0];

        return (
          <div key={`${d.label}-${i}`}>
            <div className="mb-1 flex items-baseline justify-between gap-3 text-xs">
              <span className="min-w-0 truncate text-muted-foreground">
                {d.label}
                {d.hint && <span className="ml-1 text-muted-foreground/70">· {d.hint}</span>}
              </span>
              <span className="shrink-0 font-semibold tabular-nums text-foreground">
                {format(d.value)}
              </span>
            </div>
            <div
              className="h-2.5 w-full overflow-hidden rounded-sm"
              style={{ backgroundColor: chartColors.grid }}
            >
              {/* Rounded on the DATA end only — the baseline end is anchored,
                  and rounding it would suggest the bar starts somewhere after
                  zero. */}
              <div
                className="h-full rounded-r-sm transition-all duration-300"
                style={{ width: `${width}%`, backgroundColor: fill }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
